'use client';

import Link from '@/components/compat/Link';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { GsImage } from '@/components/help-center/latest-power-shared';
import { formatPowerDisplayName } from '@/lib/power-mention-data';
import type { FairtradePowerRow } from '@/lib/fairtrade-catalog';
import {
  getFairtradeRowCategoryDisplay,
  getFairtradeRowDisplayPrices,
  type FairtradeCollectionPriceStats,
} from '@/lib/fairtrade-catalog';
import {
  FairtradeAvailabilityBadge,
  FairtradeCategoryBadge,
  FairtradeDaysValue,
  FairtradeIdBadge,
  FairtradeNewBadge,
  FairtradeStoreValue,
  FairtradeXatsValue,
} from '@/components/help-center/fairtrade/fairtrade-shared';
import PowerFairtradeHistoryDialog from '@/components/help-center/powers/PowerFairtradeHistoryDialog';
import FairtradeCollectionActions from '@/components/help-center/fairtrade/FairtradeCollectionActions';
import {
  fairtradeSortColumnOf,
  fairtradeSortIsAsc,
  toggleFairtradeColumnSort,
  type FairtradeSortColumn,
  type FairtradeSortId,
} from '@/lib/fairtrade-query';
import { cn } from '@/lib/utils';

function historySubject(row: FairtradePowerRow) {
  return {
    slug: row.slug,
    name: formatPowerDisplayName(row.name),
    prices: {
      fairtrade_min: row.minXats,
      fairtrade_max: row.maxXats,
      fairtrade_min_days: row.minDays,
      fairtrade_max_days: row.maxDays,
    },
  };
}

type HeaderCol = {
  key: string;
  label: string;
  column?: FairtradeSortColumn;
  align?: 'left' | 'right';
  tone?: 'default' | 'store';
};

const HEADER_COLS: HeaderCol[] = [
  { key: 'id', label: 'ID', column: 'id' },
  { key: 'power', label: 'Power', column: 'power' },
  { key: 'category', label: 'Categoria', column: 'category' },
  { key: 'status', label: 'Status', column: 'status' },
  { key: 'xats', label: 'Preço em xats', column: 'xats' },
  { key: 'days', label: 'Preço em days', column: 'days' },
  { key: 'store', label: 'Loja', column: 'store', tone: 'store' },
  { key: 'actions', label: 'Ações', align: 'right' },
];

function SortableTh({
  col,
  sort,
  onSort,
  isDark,
}: {
  col: HeaderCol;
  sort: FairtradeSortId;
  onSort?: (next: FairtradeSortId) => void;
  isDark: boolean;
}) {
  const activeColumn = fairtradeSortColumnOf(sort);
  const isActive = Boolean(col.column && activeColumn === col.column);
  const isAsc = fairtradeSortIsAsc(sort);
  const sortable = Boolean(col.column && onSort);

  const content = (
    <>
      <span>{col.label}</span>
      {sortable ? (
        <span
          className={cn(
            'inline-flex h-4 w-4 items-center justify-center rounded',
            isActive
              ? isDark
                ? 'text-sky-200'
                : 'text-sky-700'
              : isDark
                ? 'text-sky-200/35'
                : 'text-slate-400'
          )}
          aria-hidden
        >
          {isActive ? (
            isAsc ? (
              <ArrowUp className="h-3 w-3" />
            ) : (
              <ArrowDown className="h-3 w-3" />
            )
          ) : (
            <ArrowUpDown className="h-3 w-3" />
          )}
        </span>
      ) : null}
    </>
  );

  return (
    <th
      scope="col"
      className={cn(
        'py-3 last:pr-5',
        col.key === 'id'
          ? 'w-[1%] whitespace-nowrap pl-5 pr-1.5'
          : col.key === 'power'
            ? 'pl-1.5 pr-3 sm:pr-4'
            : 'px-3 first:pl-5 sm:px-4',
        col.align === 'right' && 'text-right'
      )}
    >
      {sortable ? (
        <button
          type="button"
          onClick={() => onSort?.(toggleFairtradeColumnSort(col.column!, sort))}
          className={cn(
            'group inline-flex max-w-full items-center gap-1.5 rounded-lg px-0.5 py-1',
            'font-poppins text-[11px] font-semibold uppercase tracking-[0.12em]',
            'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40',
            col.tone === 'store'
              ? isActive
                ? isDark
                  ? 'text-violet-100'
                  : 'text-violet-800'
                : isDark
                  ? 'text-violet-200/80 hover:text-violet-100'
                  : 'text-violet-600 hover:text-violet-800'
              : isActive
                ? isDark
                  ? 'text-sky-50'
                  : 'text-sky-900'
                : isDark
                  ? 'text-sky-200/75 hover:text-sky-50'
                  : 'text-slate-500 hover:text-slate-800'
          )}
          aria-sort={isActive ? (isAsc ? 'ascending' : 'descending') : 'none'}
          title={`Ordenar por ${col.label}`}
        >
          {content}
        </button>
      ) : (
        <span
          className={cn(
            'inline-flex items-center font-poppins text-[11px] font-semibold uppercase tracking-[0.12em]',
            isDark ? 'text-sky-200/60' : 'text-slate-400'
          )}
        >
          {content}
        </span>
      )}
    </th>
  );
}

export default function FairtradeTable({
  rows,
  isDark = false,
  activeFilter = '',
  latestPowerId = null,
  sort = 'id-desc',
  onSortChange,
  collectionPrices,
}: {
  rows: FairtradePowerRow[];
  isDark?: boolean;
  /** Filtro activo (categoria ou família de coleção). */
  activeFilter?: string;
  /** ID do último power lançado — recebe destaque na lista. */
  latestPowerId?: number | null;
  sort?: FairtradeSortId;
  onSortChange?: (sort: FairtradeSortId) => void;
  collectionPrices?: Map<string, FairtradeCollectionPriceStats>;
}) {
  return (
    <div className={cn('hidden md:block', isDark ? 'text-sky-50' : 'text-gray-900')}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1040px] border-collapse text-left">
          <thead>
            <tr
              className={cn(
                'border-y',
                isDark
                  ? 'border-sky-500/20 bg-gradient-to-r from-sky-500/[0.12] via-[#030023]/80 to-violet-500/[0.08]'
                  : 'border-sky-100 bg-gradient-to-r from-sky-50 via-white to-violet-50/60'
              )}
            >
              {HEADER_COLS.map((col) => (
                <SortableTh
                  key={col.key}
                  col={col}
                  sort={sort}
                  onSort={onSortChange}
                  isDark={isDark}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const categoryDisplay = getFairtradeRowCategoryDisplay(row, activeFilter);
              const isLatest = latestPowerId != null && row.id === latestPowerId;
              const prices = getFairtradeRowDisplayPrices(row, collectionPrices);
              return (
                <tr
                  key={row.id}
                  className={cn(
                    'border-b transition-colors last:border-0',
                    isLatest
                      ? isDark
                        ? 'border-sky-400/25 bg-gradient-to-r from-sky-500/[0.14] via-sky-500/[0.06] to-transparent'
                        : 'border-sky-200 bg-gradient-to-r from-sky-50 via-sky-50/70 to-white'
                      : isDark
                        ? 'border-blue-900/40 hover:bg-sky-500/[0.06]'
                        : 'border-gray-50 hover:bg-sky-50/50'
                  )}
                >
                  <td className="w-[1%] whitespace-nowrap py-3 pl-5 pr-1.5">
                    <Link
                      href={`/powers/${encodeURIComponent(row.slug)}`}
                      className="inline-flex rounded-lg no-underline outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40"
                      aria-label={`ID ${row.id}, abrir ${formatPowerDisplayName(row.name)}`}
                    >
                      <FairtradeIdBadge id={row.id} isDark={isDark} />
                    </Link>
                  </td>
                  <td className="py-3 pl-1.5 pr-3 sm:pr-4">
                    <Link
                      href={`/powers/${encodeURIComponent(row.slug)}`}
                      className={cn(
                        'group flex items-center gap-2.5 rounded-lg no-underline outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40',
                        isDark ? 'text-sky-50' : 'text-gray-900'
                      )}
                    >
                      <GsImage
                        code={row.name}
                        className="flex h-9 w-9 shrink-0 items-center justify-center"
                        imgClassName="max-h-9 max-w-9 object-contain drop-shadow-sm"
                        sizePx={36}
                      />
                      <div className="min-w-0">
                        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                          <p
                            className={cn(
                              'truncate font-poppins text-sm font-semibold transition-colors',
                              isDark
                                ? '!text-sky-50 group-hover:!text-sky-300'
                                : '!text-gray-900 group-hover:!text-sky-800'
                            )}
                          >
                            {formatPowerDisplayName(row.name)}
                          </p>
                          {isLatest ? <FairtradeNewBadge isDark={isDark} /> : null}
                        </div>
                        {row.title ? (
                          <p
                            className={cn(
                              'truncate font-inter text-xs',
                              isDark ? '!text-blue-200/75' : '!text-gray-500'
                            )}
                          >
                            {row.title}
                          </p>
                        ) : null}
                      </div>
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <FairtradeCategoryBadge
                      type={row.type}
                      typeLabel={categoryDisplay.label}
                      category={categoryDisplay.categoryKey}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <FairtradeAvailabilityBadge limited={row.flags.limited} isDark={isDark} />
                  </td>
                  <td className="px-4 py-3">
                    <FairtradeXatsValue
                      min={prices.minXats}
                      max={prices.maxXats}
                      isDark={isDark}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <FairtradeDaysValue
                      min={prices.minDays}
                      max={prices.maxDays}
                      isDark={isDark}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <FairtradeStoreValue
                      storePrice={row.storePrice}
                      isDark={isDark}
                      accent
                    />
                  </td>
                  <td className="px-4 py-3 last:pr-5">
                    <div className="flex items-center justify-end gap-1.5">
                      <FairtradeCollectionActions row={row} isDark={isDark} />
                      <PowerFairtradeHistoryDialog
                        power={historySubject(row)}
                        triggerClassName={
                          isDark
                            ? undefined
                            : 'border-sky-200 bg-sky-50 text-sky-700 hover:border-sky-300 hover:bg-sky-100 hover:text-sky-800'
                        }
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
